import api from './api';

/**
 * Serve Position Service
 * Handles all API calls for serve position management
 */

const servePositionService = {
  // Serve Position CRUD operations
  
  /**
   * Get all serve positions with pagination and filtering
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with positions data
   */
  getPositions: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/serve-positions/positions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching serve positions:', error);
      throw error;
    }
  },

  /**
   * Get a single serve position by ID
   * @param {number} positionId - Position ID
   * @returns {Promise} Position data
   */
  getPositionById: async (positionId) => {
    try {
      const response = await api.get(`/api/v1/serve-positions/positions/${positionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching position ${positionId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new serve position
   * @param {Object} positionData - Position data
   * @returns {Promise} Created position data
   */
  createPosition: async (positionData) => {
    try {
      const response = await api.post('/api/v1/serve-positions/positions', positionData);
      return response.data;
    } catch (error) {
      console.error('Error creating serve position:', error);
      throw error;
    }
  },

  /**
   * Update a serve position
   * @param {number} positionId - Position ID
   * @param {Object} positionData - Updated position data
   * @returns {Promise} Updated position data
   */
  updatePosition: async (positionId, positionData) => {
    try {
      const response = await api.put(`/api/v1/serve-positions/positions/${positionId}`, positionData);
      return response.data;
    } catch (error) {
      console.error(`Error updating position ${positionId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a serve position
   * @param {number} positionId - Position ID
   * @returns {Promise} Response
   */
  deletePosition: async (positionId) => {
    try {
      const response = await api.delete(`/api/v1/serve-positions/positions/${positionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting position ${positionId}:`, error);
      throw error;
    }
  },

  /**
   * Get all position categories
   * @returns {Promise} List of categories
   */
  getCategories: async () => {
    try {
      const response = await api.get('/api/v1/serve-positions/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Assignment operations
  
  /**
   * Get all serve position assignments with filtering
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with assignments data
   */
  getAssignments: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/serve-positions/assignments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error;
    }
  },

  /**
   * Create a new serve position assignment
   * @param {Object} assignmentData - Assignment data
   * @returns {Promise} Created assignment data
   */
  createAssignment: async (assignmentData) => {
    try {
      const response = await api.post('/api/v1/serve-positions/assignments', assignmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating assignment:', error);
      throw error;
    }
  },

  /**
   * Update a serve position assignment
   * @param {number} assignmentId - Assignment ID
   * @param {Object} assignmentData - Updated assignment data
   * @returns {Promise} Updated assignment data
   */
  updateAssignment: async (assignmentId, assignmentData) => {
    try {
      const response = await api.put(`/api/v1/serve-positions/assignments/${assignmentId}`, assignmentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating assignment ${assignmentId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a serve position assignment
   * @param {number} assignmentId - Assignment ID
   * @returns {Promise} Response
   */
  deleteAssignment: async (assignmentId) => {
    try {
      const response = await api.delete(`/api/v1/serve-positions/assignments/${assignmentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting assignment ${assignmentId}:`, error);
      throw error;
    }
  },

  // Servants operations
  
  /**
   * Get all servants (members with worship ID)
   * @param {Object} params - Query parameters
   * @returns {Promise} Response with servants data
   */
  getServants: async (params = {}) => {
    try {
      const response = await api.get('/api/v1/serve-positions/servants', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching servants:', error);
      throw error;
    }
  },
};

export default servePositionService;
